<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetHandoverProtocol',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    form: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const units = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    units: units.value,
    ux: ux.value,
  }));

  const $$load_units = () => {
    return $fn.value.fetch('get_meter_type_units');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'units' }, { name: 'ux' }],
    loaders: { units: $$load_units },
    ux: {},
    dataRefs: { units, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.form,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('form');
    }
  );

  const act_1759621513499166_script_jultxz88k_value_callable = async (
    $event
  ) => {
    return (props.item.meter_readings = _.pull(
      props.item.meter_readings,
      $event.scope.reading
    ).slice());
  };
  const act_1759621513499166_script_jultxz88k_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1759621513499166_script_jultxz88k_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_xglwf4d43 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_1df2xn6ss =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_xglwf4d43,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_1df2xn6ss],
    event: 'reload',
  });
  const act_1759621513499166_script_jultxz88k = new actionTypes.ScriptAction({
    actionArgs: act_1759621513499166_script_jultxz88k_getActionArgs,
  });
  const inst_evh_1759621513499166_clickPreventStop_jgvs6pqea =
    new actionTypes.EventHandlerImpl({
      action: act_1759621513499166_script_jultxz88k,
      event: 'click.prevent.stop',
      displayName: 'script',
    });
  const inst_evh_1759621513499166_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1759621513499166_clickPreventStop_jgvs6pqea],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_1df2xn6ss,
    inst_evh_7315092382398562_reload,
    inst_evh_1759621513499166_clickPreventStop_jgvs6pqea,
    inst_evh_1759621513499166_clickPreventStop,
  };

  const evh_7315092382398562_reload_1df2xn6ss = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_1df2xn6ss.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1759621513499166_clickPreventStop_jgvs6pqea = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1759621513499166_clickPreventStop_jgvs6pqea.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1759621513499166_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1759621513499166_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      :label-cols-md="2"
      :content-cols-md="3"
      name="protocol_status"
      :label="pgettext('RentalHandover', 'Status')"
      :labelAlign="labelAlign"
    >
      <UcStatusBadge :status="item.protocol_status"> </UcStatusBadge>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="2"
      :content-cols-md="3"
      name="handover_date"
      :label="pgettext('RentalHandover', 'Handover date')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormDatetime
        name="handover_date"
        v-model="item.handover_date"
        required
        :readonly="item.isReadonly"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="2"
      :content-cols-md="10"
      name="meter_readings"
      :label="pgettext('RentalHandover', 'Paragraphs')"
      :labelAlign="labelAlign"
      v-if="loaded && item.paragraphs"
    >
      <div :key="index" v-for="(paragraph, index) in item.paragraphs">
        <ItpFormTextarea
          type="number"
          name="paragraph_text"
          v-model="paragraph.text"
          required
          :readonly="item.isReadonly"
          class="w-100"
        >
        </ItpFormTextarea>
      </div>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="2"
      :content-cols-md="10"
      name="meter_readings"
      :label="pgettext('RentalHandover', 'Meter reading')"
      :labelAlign="labelAlign"
      v-if="loaded && item.meter_readings && item.meter_readings.length"
    >
      <table class="table table-condensed">
        <tbody>
          <tr
            :key="reading.serial_number"
            v-for="reading in item.meter_readings"
          >
            <td class="col-sm-7" style="word-break: break-all">
              <ItpBox>
                <UcMeterTypeIcon :type="reading.meter_type" class="me-2">
                </UcMeterTypeIcon>
                <ItpText :text="reading.serial_number" wrap> </ItpText>
              </ItpBox>
              <ItpText :text="reading.description" wrap> </ItpText>
              <ItpLink
                :text="gettext('Remove')"
                @click.prevent.stop="
                  evh_1759621513499166_clickPreventStop($event, { reading })
                "
              >
              </ItpLink>
            </td>
            <td class="col-sm-5">
              <div class="input-group">
                <ItpFormInput
                  mask="number"
                  unmask="typed"
                  :min="0"
                  name="value"
                  v-model="reading.value"
                  :readonly="item.isReadonly"
                >
                </ItpFormInput>
                <ItpFormNativeSelect
                  name="unit"
                  v-model="reading.unit"
                  :options="units[reading.meter_type]"
                  :readonly="item.isReadonly"
                >
                </ItpFormNativeSelect>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </ItpFormGroup>
    <UcAttachmentsList
      :parent="item"
      :readonly="item.isReadonly"
      v-if="!item.isNew"
    >
    </UcAttachmentsList>
  </ItpUsercontrol>
</template>
